<template>
  <div ref="rootEl" class="w-full">
    <button
      type="button"
      class="flex w-full items-center gap-2 rounded-md bg-default px-2.5 py-1.5 text-sm text-highlighted ring ring-inset ring-accented hover:ring-inverted focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      @click.stop.prevent="toggle"
    >
      <UIcon name="i-heroicons-calendar-days" class="size-5 shrink-0 text-dimmed" />
      <span class="truncate flex-1 text-left font-normal">{{ buttonLabel }}</span>
      <UIcon
        name="i-heroicons-chevron-down-20-solid"
        class="size-5 shrink-0 text-dimmed transition-transform"
        :class="open ? 'rotate-180' : ''"
      />
    </button>

    <div
      v-show="open"
      class="mt-2 rounded-lg border border-default bg-default p-2 shadow-sm"
      :class="{ 'complex-date-range': isRange }"
      @click.stop
    >
      <ClientOnly>
        <UCalendar
          v-if="isRange"
          :key="calendarKey"
          :model-value="localRange"
          range
          prevent-deselect
          :fixed-date="pickingEnd ? 'start' : undefined"
          class="w-full"
          @update:model-value="onRangeSelect"
        />
        <UCalendar
          v-else
          :model-value="singleModel"
          class="w-full"
          @update:model-value="onSingleSelect"
        />
        <template #fallback>
          <p class="text-sm text-muted p-2">Se încarcă calendarul...</p>
        </template>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CalendarDate,
  DateFormatter,
  getLocalTimeZone,
  type DateValue,
} from '@internationalized/date'
import { onClickOutside } from '@vueuse/core'

const props = defineProps<{
  modelValue?: string | { start?: string; end?: string } | null
  range?: boolean
  placeholder?: string
  minDays?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | { start: string; end: string } | null]
}>()

const open = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const calendarKey = ref(0)
const pickingEnd = ref(false)
const localRange = ref<{ start: CalendarDate, end: CalendarDate } | undefined>()
const df = new DateFormatter('ro-RO', { dateStyle: 'medium' })

const isRange = computed(() => Boolean(props.range))
const minDays = computed(() => Math.max(1, Math.floor(Number(props.minDays) || 1)))

onClickOutside(rootEl, () => {
  if (open.value) open.value = false
  pickingEnd.value = false
})

function parseIso(value?: string | null): CalendarDate | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined
  return new CalendarDate(year, month, day)
}

function toIso(value?: DateValue | null): string {
  if (!value) return ''
  return `${value.year}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`
}

function formatIso(value?: string | null): string {
  const parsed = parseIso(value)
  if (!parsed) return ''
  return df.format(parsed.toDate(getLocalTimeZone()))
}

function asCalendarDate(value: DateValue): CalendarDate {
  return new CalendarDate(value.year, value.month, value.day)
}

function snapRange(start: DateValue, end: DateValue): { start: CalendarDate, end: CalendarDate } {
  let nextStart = asCalendarDate(start)
  let nextEnd = asCalendarDate(end)
  if (nextEnd.compare(nextStart) < 0) {
    const swapped = nextStart
    nextStart = nextEnd
    nextEnd = swapped
  }

  const extraDays = minDays.value - 1
  if (extraDays > 0) {
    const minEnd = asCalendarDate(nextStart.add({ days: extraDays }))
    if (nextEnd.compare(minEnd) < 0) {
      nextEnd = minEnd
    }
  }

  return { start: nextStart, end: nextEnd }
}

function sameRange(
  a: { start: CalendarDate, end: CalendarDate } | undefined,
  b: { start: CalendarDate, end: CalendarDate },
) {
  return Boolean(a && a.start.compare(b.start) === 0 && a.end.compare(b.end) === 0)
}

function applyRange(range: { start: CalendarDate, end: CalendarDate }, remount: boolean) {
  localRange.value = range
  if (remount) calendarKey.value += 1
  emit('update:modelValue', {
    start: toIso(range.start),
    end: toIso(range.end),
  })
}

watch(
  () => props.modelValue,
  (value) => {
    if (!isRange.value) return
    if (!value || typeof value === 'string') {
      localRange.value = undefined
      return
    }
    const start = parseIso(value.start)
    const end = parseIso(value.end)
    if (!start || !end) {
      localRange.value = undefined
      return
    }
    if (sameRange(localRange.value, { start, end })) return
    localRange.value = { start, end }
  },
  { immediate: true, deep: true },
)

const singleModel = computed(() => {
  if (isRange.value) return undefined
  return parseIso(typeof props.modelValue === 'string' ? props.modelValue : null)
})

const buttonLabel = computed(() => {
  if (isRange.value) {
    const range = typeof props.modelValue === 'object' && props.modelValue
      ? props.modelValue
      : { start: '', end: '' }
    if (range.start && range.end) {
      return `${formatIso(range.start)} – ${formatIso(range.end)}`
    }
    if (range.start) return `${formatIso(range.start)} – …`
    return props.placeholder || 'Selectează intervalul'
  }

  const single = typeof props.modelValue === 'string' ? props.modelValue : ''
  if (single) return formatIso(single)
  return props.placeholder || 'Selectează data'
})

function toggle() {
  open.value = !open.value
  if (!open.value) pickingEnd.value = false
}

function onSingleSelect(value: DateValue | DateValue[] | null | undefined) {
  if (!value || Array.isArray(value)) {
    emit('update:modelValue', null)
    return
  }
  emit('update:modelValue', toIso(value))
  open.value = false
}

function onRangeSelect(
  value: DateValue | { start?: DateValue, end?: DateValue } | DateValue[] | null | undefined,
) {
  if (!value || Array.isArray(value) || !('start' in value) || !value.start) {
    return
  }

  const snapped = snapRange(value.start, value.end || value.start)
  if (sameRange(localRange.value, snapped)) return

  const incomplete = !value.end || value.start.compare(value.end) === 0

  if (incomplete && minDays.value > 1) {
    pickingEnd.value = true
    applyRange(snapped, true)
    return
  }

  pickingEnd.value = false
  applyRange(snapped, false)
  open.value = false
}
</script>

<style scoped>
.complex-date-range :deep(.relative.text-center) {
  width: 100%;
}

.complex-date-range :deep(.relative.text-center:has([data-selected]))::before,
.complex-date-range :deep(.relative.text-center:has([data-highlighted]))::before {
  content: "";
  position: absolute;
  inset-block: 0.4rem;
  inset-inline: 0;
  background: color-mix(in srgb, var(--ui-primary) 16%, transparent);
  pointer-events: none;
}

.complex-date-range :deep(.relative.text-center:has([data-selection-start]))::before,
.complex-date-range :deep(.relative.text-center:has([data-highlighted-start]))::before {
  inset-inline-start: 50%;
}

.complex-date-range :deep(.relative.text-center:has([data-selection-end]))::before,
.complex-date-range :deep(.relative.text-center:has([data-highlighted-end]))::before {
  inset-inline-end: 50%;
}

.complex-date-range :deep([data-reka-calendar-cell-trigger][data-selected]:not([data-selection-start]):not([data-selection-end])),
.complex-date-range :deep([data-reka-calendar-cell-trigger][data-highlighted]:not([data-highlighted-start]):not([data-highlighted-end])) {
  background-color: transparent;
  color: inherit;
}
</style>
